import { useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatInput } from "@/components/chat/ChatInput";
import { ToolUseIndicator } from "@/components/chat/blocks/ToolUseIndicator";
import type { ChatMessage as ChatMessageType, ConnectionStatus } from "@/lib/chat-types";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";

interface ChatContainerProps {
  messages: ChatMessageType[];
  status: ConnectionStatus;
  placeholder?: string;
  onSend: (payload: { content: string; mode: "qa" | "review"; attachments?: FundComplianceFileRef[] }) => void;
  onAttachFile?: (file: File) => void;
  onClearAttachment?: (index: number) => void;
  composerMode?: "qa" | "review";
  pendingAttachments?: FundComplianceFileRef[];
  onLoadMore?: () => void;
  hasMoreHistory?: boolean;
  loadingMore?: boolean;
  /** Show the centered "How can I help..." landing layout instead of the chat view. */
  isEmptyState?: boolean;
  /** Latest tool name from a tool_use event; null to hide the indicator. */
  toolUse?: string | null;
}

/** Distance from top to trigger loading more history (px). */
const LOAD_MORE_THRESHOLD = 80;
const SMOOTH_SCROLL_INTERVAL_MS = 600;

function ShieldIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

export function ChatContainer({
  messages,
  status,
  placeholder,
  onSend,
  onAttachFile,
  onClearAttachment,
  composerMode = "qa",
  pendingAttachments,
  onLoadMore,
  hasMoreHistory = false,
  loadingMore = false,
  isEmptyState = false,
  toolUse = null,
}: ChatContainerProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messageRenderSignature = messages
    .map((message) =>
      `${message.id}:${message.blocks
        .map((block) => (block.type === "text" ? `${block.type}:${block.content?.length ?? 0}` : block.type))
        .join(",")}`
    )
    .join("|");

  // Track first message id to detect prepends for scroll-position preservation
  const firstMessageIdRef = useRef<string | null>(messages[0]?.id ?? null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const previousActiveMessageIdRef = useRef<string | null>(messages[messages.length - 1]?.id ?? null);
  const previousMessageCountRef = useRef(messages.length);
  const previousConversationSignatureRef = useRef<string>(messages.map((message) => message.id).join("|"));
  const lastSmoothScrollAtRef = useRef(0);

  // Capture scroll height BEFORE the DOM updates if the first message changed (prepend)
  const firstId = messages[0]?.id ?? null;
  if (firstMessageIdRef.current !== firstId && scrollAreaRef.current && firstMessageIdRef.current !== null) {
    prevScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
  }

  // After layout: restore scroll position on prepend, or auto-scroll on append
  useLayoutEffect(() => {
    if (isEmptyState) return;
    const area = scrollAreaRef.current;
    if (!area) return;

    // If messages were prepended, preserve the scroll position
    if (prevScrollHeightRef.current !== null && firstMessageIdRef.current !== firstId) {
      const diff = area.scrollHeight - prevScrollHeightRef.current;
      area.scrollTop += diff;
      prevScrollHeightRef.current = null;
      firstMessageIdRef.current = firstId;
      return;
    }

    firstMessageIdRef.current = firstId;
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messageRenderSignature, firstId, isEmptyState, toolUse]);

  useEffect(() => {
    if (isEmptyState) return;
    const latestMessageId = messages[messages.length - 1]?.id ?? null;
    const messageCount = messages.length;
    const conversationSignature = messages.map((message) => message.id).join("|");

    const latestChanged = latestMessageId !== previousActiveMessageIdRef.current;
    const appendedMessage = messageCount > previousMessageCountRef.current;
    const conversationChanged = conversationSignature !== previousConversationSignatureRef.current && latestChanged;

    if (conversationChanged) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    } else if (appendedMessage && latestChanged) {
      const now = Date.now();
      if (now - lastSmoothScrollAtRef.current >= SMOOTH_SCROLL_INTERVAL_MS) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        lastSmoothScrollAtRef.current = now;
      }
    }

    previousActiveMessageIdRef.current = latestMessageId;
    previousMessageCountRef.current = messageCount;
    previousConversationSignatureRef.current = conversationSignature;
  }, [isEmptyState, messages]);

  // Scroll handler: trigger load-more when scrolled near the top
  useEffect(() => {
    if (isEmptyState) return;
    const area = scrollAreaRef.current;
    if (!area || !onLoadMore) return;

    function handleScroll() {
      if (!area || !onLoadMore) return;
      if (loadingMore || !hasMoreHistory) return;
      if (area.scrollTop <= LOAD_MORE_THRESHOLD) {
        onLoadMore();
      }
    }

    area.addEventListener("scroll", handleScroll, { passive: true });
    return () => area.removeEventListener("scroll", handleScroll);
  }, [onLoadMore, hasMoreHistory, loadingMore, isEmptyState]);

  const handleFollowUp = useCallback(
    (text: string) => {
      onSend({ content: text, mode: composerMode, attachments: pendingAttachments });
    },
    [composerMode, onSend, pendingAttachments]
  );

  /* ── Empty state layout ────────────────────────────────────────── */

  if (isEmptyState) {
    return (
      <div className="relative z-10 flex flex-col flex-1 min-h-0 animate-[fadeSlideIn_240ms_ease-out]">
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-10">
          <div className="w-full max-w-[640px] flex flex-col items-center text-center">
            <div className="w-12 h-12 mb-5 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] flex items-center justify-center text-[var(--color-accent)]">
              <ShieldIcon />
            </div>
            <h1
              className="text-[22px] sm:text-[24px] font-semibold text-[var(--color-fg)] leading-tight mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              How can I help with MAS compliance?
            </h1>
            <p className="text-sm text-[var(--color-fg-secondary)] mb-6">
              Ask a question or attach a factsheet for a full compliance review.
            </p>
            <div className="w-full">
              <ChatInput
                onSend={(content) => onSend({ content, mode: composerMode, attachments: pendingAttachments })}
                onAttachFile={onAttachFile}
                onClearAttachment={onClearAttachment}
                attachments={pendingAttachments}
                disabled={false}
                sendDisabled={false}
                placeholder="Ask anything about MAS compliance, or attach a PDF…"
                preserveValueOnSend
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Normal chat layout ────────────────────────────────────────── */

  return (
    <div className="relative z-10 flex flex-col flex-1 min-h-0 animate-[fadeSlideIn_240ms_ease-out]">
      {/* Connection status — absolute overlay, does not affect layout */}
      {status !== "connected" && (
        <div
          className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)]/90 backdrop-blur-sm text-[10px] tracking-widest uppercase text-[var(--color-fg-tertiary)] shadow-sm"
          style={{ fontFamily: "var(--font-mono)" }}
        >
          {status === "connecting" && "Connecting…"}
          {status === "disconnected" && "Disconnected"}
          {status === "error" && "Connection error"}
        </div>
      )}

      {/* Messages area */}
      <div ref={scrollAreaRef} className="flex-1 overflow-y-auto px-3 py-6 sm:px-5">
        <div className="max-w-[800px] mx-auto">
          {loadingMore && (
            <div className="flex justify-center py-2 text-[10px] tracking-widest uppercase text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>
              Loading earlier messages…
            </div>
          )}
          {messages.map((msg, index) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onFollowUp={handleFollowUp}
              isLatestMessage={index === messages.length - 1}
            />
          ))}
          {toolUse && <ToolUseIndicator toolName={toolUse} />}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <ChatInput
        onSend={(content) => onSend({ content, mode: composerMode, attachments: pendingAttachments })}
        onAttachFile={onAttachFile}
        onClearAttachment={onClearAttachment}
        attachments={pendingAttachments}
        disabled={false}
        sendDisabled={false}
        placeholder={placeholder}
      />
    </div>
  );
}
